import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReceiptText, Printer, TrendingUp, Wallet, AlertCircle } from 'lucide-react';
import { useOrgId } from '../../hooks/useOrgId';
import { useAuth } from '../../context/AuthContext';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card, StatCard } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { formatXOF } from '../../lib/utils';
import { toast } from 'sonner';

const money = (v) => `${formatXOF(v)} FCFA`;

export default function FinanceStatementsPage() {
  const orgId = useOrgId();
  const { currentOrg } = useAuth();
  const { selectedYearId } = useAcademicYear();
  const [params, setParams] = useSearchParams();
  const [guardians, setGuardians] = useState([]);
  const [guardianId, setGuardianId] = useState(params.get('guardian_id') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const sheetRef = useRef(null);

  useEffect(() => { if (orgId) schoolApi.financeGuardians(orgId).then((r) => setGuardians(Array.isArray(r) ? r : [])).catch(() => {}); }, [orgId]);

  const load = useCallback(async () => {
    if (!orgId || !guardianId) { setData(null); return; }
    setLoading(true);
    try {
      setData(await schoolApi.guardianStatement(orgId, { guardian_id: guardianId, academic_year_id: selectedYearId }));
    } catch { toast.error('Failed to load statement'); setData(null); }
    finally { setLoading(false); }
  }, [orgId, guardianId, selectedYearId]);
  useEffect(() => { load(); }, [load]);

  const onSelect = (v) => {
    setGuardianId(v);
    const next = new URLSearchParams(params);
    if (v) next.set('guardian_id', v); else next.delete('guardian_id');
    setParams(next, { replace: true });
  };

  const printPdf = async () => {
    if (!sheetRef.current) return;
    const html2pdf = (await import('html2pdf.js')).default;
    const name = (data?.guardian?.name || 'statement').replace(/\s+/g, '_');
    await html2pdf().set({
      margin: 10, filename: `Statement_${name}.pdf`,
      html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(sheetRef.current).save();
  };

  const s = data?.summary || { billed: 0, paid: 0, balance: 0 };

  return (
    <div className="space-y-6" data-testid="statements-page">
      <PageHeader icon={ReceiptText} title="Guardian Statements" subtitle="Statement of account per guardian for the selected academic year" />

      <Card className="p-3 flex flex-wrap gap-3 items-center">
        <Select value={guardianId || ''} onValueChange={onSelect}>
          <SelectTrigger className="w-80" data-testid="stmt-guardian-select"><SelectValue placeholder="Select a guardian..." /></SelectTrigger>
          <SelectContent className="max-h-80">{guardians.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
        </Select>
        {data && <Button variant="outline" onClick={printPdf} className="ml-auto" data-testid="stmt-print"><Printer className="w-4 h-4 mr-1" /> Print PDF</Button>}
      </Card>

      {!guardianId ? (
        <EmptyState icon={ReceiptText} title="Select a guardian" description="Choose a guardian to view their statement of account." />
      ) : loading ? (
        <LoadingState rows={6} />
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label="Total Billed" value={money(s.billed)} icon={TrendingUp} accent="students" testId="stmt-billed" />
            <StatCard label="Total Collected" value={money(s.paid)} icon={Wallet} accent="teachers" testId="stmt-collected" />
            <StatCard label="Outstanding" value={money(s.balance)} icon={AlertCircle} accent="classes" testId="stmt-outstanding" />
          </div>

          {/* Printable sheet */}
          <Card className="p-0 overflow-hidden">
            <div ref={sheetRef} className="p-6 bg-white text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <div className="flex items-center gap-3 border-b pb-3 mb-4">
                {currentOrg?.logo_url && <img src={currentOrg.logo_url} alt="logo" style={{ width: 48, height: 48, objectFit: 'contain' }} crossOrigin="anonymous" />}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 18 }}>{currentOrg?.name || 'School'}</div>
                  <div style={{ fontSize: 12, color: '#555' }}>Statement of Account</div>
                </div>
                <div style={{ marginLeft: 'auto', textAlign: 'right', fontSize: 12 }}>
                  <div><strong>{data.guardian?.name}</strong></div>
                  {data.guardian?.code && <div>Code: {data.guardian.code}</div>}
                  {data.guardian?.mobile && <div>Tel: {data.guardian.mobile}</div>}
                  <div>Academic Year: {data.academic_year_name || '\u2014'}</div>
                </div>
              </div>

              {data.students?.length > 0 && (
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', marginBottom: 16 }}>
                  <thead><tr style={{ borderBottom: '1px solid #ccc', textAlign: 'left' }}>
                    <th style={{ padding: 6 }}>Student</th><th style={{ padding: 6, textAlign: 'right' }}>Billed</th>
                    <th style={{ padding: 6, textAlign: 'right' }}>Paid</th><th style={{ padding: 6, textAlign: 'right' }}>Balance</th>
                  </tr></thead>
                  <tbody>{data.students.map((st) => (
                    <tr key={st.student_id} style={{ borderBottom: '1px solid #eee' }} data-testid={`stmt-student-${st.student_id}`}>
                      <td style={{ padding: 6 }}>{st.student_name}</td>
                      <td style={{ padding: 6, textAlign: 'right' }}>{formatXOF(st.billed)}</td>
                      <td style={{ padding: 6, textAlign: 'right' }}>{formatXOF(st.paid)}</td>
                      <td style={{ padding: 6, textAlign: 'right' }}>{formatXOF(st.balance)}</td>
                    </tr>))}</tbody>
                </table>
              )}

              <div style={{ fontSize: 13, fontWeight: 600, margin: '4px 0 8px' }}>Transactions</div>
              {data.ledger?.length === 0 ? (
                <div style={{ fontSize: 12, color: '#777' }}>No transactions for this academic year.</div>
              ) : (
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }} data-testid="stmt-ledger-table">
                  <thead><tr style={{ borderBottom: '1px solid #ccc', textAlign: 'left' }}>
                    <th style={{ padding: 6 }}>Date</th><th style={{ padding: 6 }}>Type</th><th style={{ padding: 6 }}>Reference</th>
                    <th style={{ padding: 6 }}>Student</th><th style={{ padding: 6 }}>Details</th>
                    <th style={{ padding: 6, textAlign: 'right' }}>Charge</th><th style={{ padding: 6, textAlign: 'right' }}>Payment</th><th style={{ padding: 6, textAlign: 'right' }}>Balance</th>
                  </tr></thead>
                  <tbody>{data.ledger.map((e, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee' }} data-testid={`stmt-ledger-row-${i}`}>
                      <td style={{ padding: 6 }}>{e.date}</td>
                      <td style={{ padding: 6 }}>{e.type}</td>
                      <td style={{ padding: 6, fontFamily: 'monospace' }}>{e.ref}</td>
                      <td style={{ padding: 6 }}>{e.student_name || '\u2014'}</td>
                      <td style={{ padding: 6, color: '#555', maxWidth: 220 }}>{e.description}</td>
                      <td style={{ padding: 6, textAlign: 'right' }}>{e.debit ? formatXOF(e.debit) : '\u2014'}</td>
                      <td style={{ padding: 6, textAlign: 'right', color: '#047857' }}>{e.credit ? formatXOF(e.credit) : '\u2014'}</td>
                      <td style={{ padding: 6, textAlign: 'right', fontWeight: 600 }}>{formatXOF(e.balance)}</td>
                    </tr>))}</tbody>
                  <tfoot><tr style={{ borderTop: '2px solid #333', fontWeight: 700 }}>
                    <td style={{ padding: 6 }} colSpan={5}>Totals</td>
                    <td style={{ padding: 6, textAlign: 'right' }}>{formatXOF(s.billed)}</td>
                    <td style={{ padding: 6, textAlign: 'right', color: '#047857' }}>{formatXOF(s.paid)}</td>
                    <td style={{ padding: 6, textAlign: 'right' }}>{formatXOF(s.balance)}</td>
                  </tr></tfoot>
                </table>
              )}
              <div style={{ marginTop: 16, fontSize: 13 }}>Outstanding balance: <strong>{money(s.balance)}</strong></div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
